import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode, signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase-config"; // Firebase 認証設定を読み込む
import { postRequest } from "../apiWrapper"; // APIラッパー
import getBrandFromHostname from "../utils/getBrandFromHostname"; // ブランド取得関数
import { HOSTS } from "../constants/hosts";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [oobCode, setOobCode] = useState(null);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const code = searchParams.get("oobCode"); // URL から oobCode を取得
    if (!code) {
      setErrorMessage("無効なリンクです。");
      return;
    }
    setOobCode(code);

    // 🔹 `oobCode` を検証して、対応するメールアドレスを取得
    verifyPasswordResetCode(auth, code)
      .then((email) => {
        setEmail(email);
      })
      .catch(() => {
        setErrorMessage("リンクが無効または期限切れです。");
      });
  }, [searchParams]);

  const validatePassword = (password) => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{12,}$/.test(password);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!validatePassword(newPassword)) {
      setErrorMessage("パスワードは12文字以上で、英大文字・英小文字・数字を含める必要があります。");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("確認用パスワードが一致しません。");
      return;
    }

    try {
      // 🔹 Firebase の `confirmPasswordReset` を使用してパスワードをリセット
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccessMessage("パスワードが正常に設定されました。ログイン処理を行います...");

      // 🔹 リセット後に自動ログイン
      const userCredential = await signInWithEmailAndPassword(auth, email, newPassword);
      const user = userCredential.user;

      // 🔹 カスタムクレームを取得
      const idTokenResult = await user.getIdTokenResult();
      const role = idTokenResult.claims.role;

      if (!role) {
        throw new Error("ロール情報が取得できません");
      }

      if (role === "teacher") {
        throw new Error("教師アカウントは別のログインページを使用してください");
      }

      if (role !== "admin") {
        throw new Error("許可されていないログインです");
      }

      // 🔹 カスタムトークン取得
      const uid = user.uid;
      const response = await postRequest("generate-token", { uid });

      const data = await response.json();
      if (!data.customToken) {
        throw new Error("カスタムトークン取得失敗:", data.error);
      }

      // 🔹 ダッシュボードへリダイレクト
      window.location.href = `https://${HOSTS.adminReport}.${getBrandFromHostname()}.classora.jp?token=${data.customToken}`; // ブランド名を取得する手段がいずれ必要
    } catch (error) {
      console.error("パスワード設定後のログインエラー:", error);
      setErrorMessage("パスワードは変更されましたが、ログインに失敗しました。ログインページから再試行してください。");
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">パスワードの登録</h1>
      <form className="bg-white p-6 rounded-lg shadow-md w-96 border border-gray-200" onSubmit={handlePasswordReset}>
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2 text-gray-700">メールアドレス</label>
          <input
            type="email"
            value={email}
            readOnly
            className="w-full px-4 py-2 border rounded-lg bg-gray-100 cursor-not-allowed"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2 text-gray-700">新しいパスワード</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
            placeholder="新しいパスワードを入力"
            required
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2 text-gray-700">新しいパスワード（確認）</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
            placeholder="確認用パスワードを入力"
            required
          />
        </div>
        {errorMessage && <p className="text-red-500 text-sm mb-4">{errorMessage}</p>}
        {successMessage && <p className="text-green-500 text-sm mb-4">{successMessage}</p>}
        <button type="submit" className="w-full bg-gray-800 text-white py-2 rounded-lg hover:bg-gray-900 transition">
          パスワードを設定してログイン
        </button>
      </form>
    </div>
  );
};

export default ResetPasswordPage;
